import React from "react";

import { BasicTooltip } from "../ui/Tooltip";

import { createValueToKeyMap, filterDamagingInputVarPairs } from "./utils";

/**
 * This function is used to highlight the matching text in a given template. It will take
 * the inputVariables and replace them in the template with a tooltip that shows the variable name.
 * It takes three parameters: text, template, and inputVariables.
 *
 * @param {string} text - The text to be highlighted.
 * @param {Record<string, string>} inputVariables - The variables to be replaced in the template.
 *
 * @returns {React.ReactNode} - Returns a formatted React Node with highlighted text if the input is valid.
 */
export const highlightRequestVariables = (
  text: string,
  inputVariables: Record<string, string>,
  maxCycles = 100, // Default maximum cycles
): React.ReactNode => {
  if (Object.keys(inputVariables).length === 0) {
    return text;
  }

  try {
    const filteredInputVariables = Object.fromEntries(filterDamagingInputVarPairs(inputVariables));
    const valueToKeyMap = createValueToKeyMap(filteredInputVariables);
    const formatted: React.ReactNode[] = [];

    let lastIndex = 0;

    const escapedKeys = Object.keys(valueToKeyMap).map(key =>
      key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    );
    const regex = new RegExp(escapedKeys.join("|"), "g");

    let match: RegExpExecArray | null;
    let cycles = 0;

    while ((match = regex.exec(text)) !== null && cycles < maxCycles) {
      const offset = match.index;
      if (lastIndex < offset) {
        formatted.push(text.substring(lastIndex, offset));
      }
      const variableName = valueToKeyMap[match[0]];
      const key = `${variableName}${offset}`;

      formatted.push(
        <BasicTooltip key={key} content={variableName}>
          <span className="font-bold text-blue-500">{match[0]}</span>
        </BasicTooltip>,
      );

      lastIndex = offset + match[0].length;
      cycles += 1;
    }

    if (lastIndex < text.length) {
      formatted.push(text.substring(lastIndex));
    }

    return <div>{formatted}</div>;
  } catch {
    return <div>{text}</div>;
  }
};
